import { FORCED_THRESHOLD, STORE_URLS, UNKNOWN_APP_VERSION } from '../constants/flag';
import {
	type AppUpdateFlag,
	type AppUpdateMode,
	type AppUpdatePlatform,
} from './appUpdateFlag';
import { compareVersions, isValidVersion } from './compareVersions';

export type AppUpdatePrompt = {
	mode: AppUpdateMode;
	platform: AppUpdatePlatform;
	appVersion: string;
	threshold: string;
	storeUrl: string;
	forced: boolean;
	simulated: boolean;
};

const LEGACY_APP_VERSION = '0.0.0';

const isAppUpdatePlatform = (value: unknown): value is AppUpdatePlatform =>
	value === 'ios' || value === 'android';

export const resolveAppPlatform = (
	reportedPlatform: unknown,
	platformOs: unknown,
	userAgent: string,
): AppUpdatePlatform => {
	if (isAppUpdatePlatform(reportedPlatform)) return reportedPlatform;
	if (isAppUpdatePlatform(platformOs)) return platformOs;
	return /android/i.test(userAgent) ? 'android' : 'ios';
};

export const decideAppUpdatePrompt = ({
	flag,
	insideApp,
	simulated,
	platform,
	appVersion,
	forcedMode,
}: {
	flag: AppUpdateFlag | null;
	insideApp: boolean;
	simulated: boolean;
	platform: AppUpdatePlatform | null;
	appVersion: string | null;
	forcedMode: AppUpdateMode | null;
}): AppUpdatePrompt | null => {
	if (forcedMode && platform) {
		return {
			mode: forcedMode,
			platform,
			appVersion: appVersion ?? UNKNOWN_APP_VERSION,
			threshold: FORCED_THRESHOLD,
			storeUrl: STORE_URLS[platform],
			forced: true,
			simulated: false,
		};
	}
	if (!flag || !(insideApp || simulated) || !platform) return null;
	const threshold = flag[platform];
	if (!threshold) return null;
	if (appVersion !== null && !isValidVersion(appVersion)) return null;
	if (compareVersions(appVersion ?? LEGACY_APP_VERSION, threshold) >= 0) return null;
	return {
		mode: flag.mode,
		platform,
		appVersion: appVersion ?? UNKNOWN_APP_VERSION,
		threshold,
		storeUrl: STORE_URLS[platform],
		forced: false,
		simulated: !insideApp,
	};
};
